import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ShieldCheck, ShieldAlert, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * AutoMatchAuditPanel
 * -------------------
 * Forensic audit log of every auto-match the recon aggregator has produced.
 * Each row is upserted by (recorded_id, bank_id) so we get first_matched_at
 * + last_seen_at timestamps and the sign-validity flag.
 *
 * Use this panel after the sign-fix deployment to PROVE there are no
 * wrong-sign matches live, and to forensically diff "yesterday's matches"
 * vs "today's matches" if anything ever looks off.
 */
export function AutoMatchAuditPanel({ API_URL, headers, investorFilter }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [wrongOnly, setWrongOnly] = useState(false);

  const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-ZA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'always',
  }));
  const fmtTs = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (investorFilter && investorFilter !== 'all') params.set('investor_id', investorFilter);
      if (wrongOnly) params.set('sign_ok', 'false');
      params.set('limit', '500');
      const r = await axios.get(
        `${API_URL}/api/admin/investor-transactions/reconciliation/audit-log?${params.toString()}`,
        { headers }
      );
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [API_URL, headers, investorFilter, wrongOnly]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data) fetchAudit();
  };

  const rows = data?.rows || [];
  const total = data?.total ?? 0;
  const wrongCount = data?.wrong_sign_count ?? 0;

  return (
    <Card className="border-slate-200" data-testid="auto-match-audit-panel">
      <CardHeader
        className="cursor-pointer select-none"
        onClick={handleToggle}
        data-testid="auto-match-audit-toggle"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {wrongCount > 0
              ? <ShieldAlert className="h-5 w-5 text-red-500" />
              : <ShieldCheck className="h-5 w-5 text-emerald-500" />}
            <div>
              <CardTitle className="text-base">Auto-Match Audit Log</CardTitle>
              <CardDescription className="text-xs">
                Forensic trail of every auto-match. {data ? (
                  <span>
                    {total} match{total === 1 ? '' : 'es'} tracked
                    {wrongCount > 0 && (
                      <span className="ml-2 text-red-600 font-semibold">
                        — {wrongCount} wrong sign
                      </span>
                    )}
                  </span>
                ) : 'Click to load.'}
              </CardDescription>
            </div>
          </div>
          {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </CardHeader>

      {open && (
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchAudit}
              disabled={loading}
              data-testid="auto-match-audit-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              variant={wrongOnly ? 'destructive' : 'outline'}
              onClick={() => { setWrongOnly(!wrongOnly); setTimeout(fetchAudit, 0); }}
              data-testid="auto-match-audit-wrong-toggle"
            >
              {wrongOnly ? 'Showing wrong-sign only' : 'Show wrong-sign only'}
            </Button>
            {investorFilter && investorFilter !== 'all' && (
              <Badge variant="secondary" className="text-xs">Investor: {investorFilter}</Badge>
            )}
          </div>

          {loading && <p className="text-sm text-slate-500">Loading…</p>}
          {!loading && rows.length === 0 && (
            <p className="text-sm text-slate-500 italic">
              No auto-matches recorded for the current filter.
            </p>
          )}
          {!loading && rows.length > 0 && (
            <div className="border rounded-md overflow-hidden">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Recorded</TableHead>
                    <TableHead className="text-right">Recorded Amt</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead className="text-right">Bank Amt</TableHead>
                    <TableHead className="text-right">Expected Bank</TableHead>
                    <TableHead>Sign</TableHead>
                    <TableHead className="text-right">Δd</TableHead>
                    <TableHead>First Matched</TableHead>
                    <TableHead>Last Seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={`${r.recorded_id}::${r.bank_id}`}
                      className={r.sign_ok ? '' : 'bg-red-50'}
                      data-testid={`audit-row-${r.recorded_id}`}
                    >
                      <TableCell className="font-mono text-[10px]" title={r.recorded_id}>
                        {(r.recorded_id || '').slice(0, 18)}…
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmt(r.recorded_amount)}</TableCell>
                      <TableCell>{r.recorded_type}</TableCell>
                      <TableCell className="font-mono text-[10px]" title={r.bank_id}>
                        {(r.bank_id || '').slice(0, 18)}…
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmt(r.bank_amount)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(r.expected_bank_val)}</TableCell>
                      <TableCell>
                        {r.sign_ok
                          ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">OK</Badge>
                          : <Badge variant="destructive">WRONG</Badge>}
                      </TableCell>
                      <TableCell className="text-right">{r.date_delta_days ?? 0}</TableCell>
                      <TableCell className="whitespace-nowrap">{fmtTs(r.first_matched_at)}</TableCell>
                      <TableCell className="whitespace-nowrap">{fmtTs(r.last_seen_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {data?.limit_applied && (
            <p className="text-xs text-amber-600">
              Showing first 500 rows. Narrow the filter to see more.
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
